import React, { useMemo } from 'react';
import { useReactTable, getCoreRowModel, ColumnDef, flexRender } from '@tanstack/react-table';

import { EditableSession } from 'types/event.types';
import { getEventDateString, getEventTimeString } from 'utils/eventUtils';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell
} from '@epoch-ui/components/Table/table';

interface SessionsTableProps {
  sessions: EditableSession[];
  emptyMessage?: React.ReactNode;
}

const SessionsTable: React.FC<SessionsTableProps> = ({ sessions, emptyMessage }) => {
  const allSessionsHaveCapacity = useMemo(
    () => sessions.every((session) => session.maxAttendees !== null),
    [sessions]
  );
  const allSessionsHaveTarget = useMemo(
    () => sessions.every((session) => session.targetAttendance !== null),
    [sessions]
  );

  const table = useReactTable({
    getCoreRowModel: getCoreRowModel(),
    data: sessions,
    columns,
    state: {
      columnVisibility: {
        capacity: allSessionsHaveCapacity,
        target: allSessionsHaveTarget
      }
    }
  });

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow
            key={headerGroup.id}
            className="tw-border-0 tw-border-b tw-border-solid tw-border-border"
          >
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id} className="tw-py-3">
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={columns.length} className="tw-h-24 tw-text-center">
              {emptyMessage}
            </TableCell>
          </TableRow>
        )}

        {table.getRowModel().rows.length > 0 &&
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className="tw-border-0 tw-border-b tw-border-solid tw-border-border"
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className="tw-py-4">
                  {cell.getValue()
                    ? flexRender(cell.column.columnDef.cell, cell.getContext())
                    : '-'}
                </TableCell>
              ))}
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );
};

export { SessionsTable };

const columns: ColumnDef<EditableSession>[] = [
  {
    id: 'title',
    header: 'Session name',
    accessorKey: 'title',
    cell: ({ row }): JSX.Element => {
      return <div className="tw-max-w-72 tw-overflow-hidden tw-truncate">{row.original.title}</div>;
    }
  },
  {
    id: 'dateTime',
    header: 'Date & time',
    accessorFn: (row) => `${getEventDateString(row)} • ${getEventTimeString(row)}`
  },
  {
    id: 'target',
    header: 'Target',
    accessorKey: 'targetAttendance'
  },
  {
    id: 'capacity',
    header: 'Capacity',
    accessorKey: 'maxAttendees'
  },
  {
    id: 'registered',
    header: 'Registered',
    accessorKey: 'guestListTotals.confirmed'
  },
  {
    id: 'attended',
    header: 'Attended',
    accessorKey: 'guestListTotals.attended'
  }
];
